import type { WhatsAppTemplate, WhatsAppTemplateButton } from '../../models/MasterAdmin/whatsappCampaign';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import { previewDeepLinkFromCampaign } from './whatsappDeepLinkPreview';

export type TemplateButtonKind = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER' | 'COPY_CODE';
export type UrlButtonMode = 'https' | 'deeplink';

export type TemplateButtonDraft =
  | { id: string; kind: 'QUICK_REPLY'; text: string }
  | {
      id: string;
      kind: 'URL';
      text: string;
      urlMode: UrlButtonMode;
      url: string;
      deepLinkCampaignId?: string | null;
      deepLinkUrlType?: 'short' | 'full' | 'dynamic';
      deepLinkParamBindings?: Record<string, string>;
    }
  | { id: string; kind: 'PHONE_NUMBER'; text: string; phoneNumber: string }
  | { id: string; kind: 'COPY_CODE'; text: string; code: string };

export const TEMPLATE_BUTTON_MAX_TOTAL = 10;
export const TEMPLATE_BUTTON_MAX_URL = 2;
export const TEMPLATE_BUTTON_MAX_PHONE = 1;
export const TEMPLATE_BUTTON_MAX_COPY_CODE = 1;
export const TEMPLATE_BUTTON_LABEL_MAX = 25;
export const TEMPLATE_FOOTER_MAX = 60;

export function newButtonId() {
  return `btn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export function emptyQuickReply(): TemplateButtonDraft {
  return { id: newButtonId(), kind: 'QUICK_REPLY', text: '' };
}

export function emptyUrlButton(): TemplateButtonDraft {
  return {
    id: newButtonId(),
    kind: 'URL',
    text: '',
    urlMode: 'deeplink',
    url: '',
    deepLinkCampaignId: null,
    deepLinkUrlType: 'short',
    deepLinkParamBindings: {},
  };
}

export function resolveUrlButtonTargetUrl(
  btn: Extract<TemplateButtonDraft, { kind: 'URL' }>,
  campaignsById?: Map<string, DeepLinkCampaign>
): string {
  if (btn.urlMode === 'deeplink') {
    if (!btn.deepLinkCampaignId || !campaignsById) return '';
    return (
      previewDeepLinkFromCampaign(
        {
          deepLinkCampaignId: btn.deepLinkCampaignId,
          deepLinkUrlType: btn.deepLinkUrlType,
          deepLinkParamBindings: btn.deepLinkParamBindings,
        },
        campaignsById
      ) || ''
    );
  }
  return String(btn.url || '').trim();
}

export function isValidTemplateButtonUrl(url: string): boolean {
  return /^https:\/\/.+/i.test(String(url || '').trim());
}

export function emptyPhoneButton(): TemplateButtonDraft {
  return { id: newButtonId(), kind: 'PHONE_NUMBER', text: '', phoneNumber: '+91' };
}

export function emptyCopyCodeButton(): TemplateButtonDraft {
  return { id: newButtonId(), kind: 'COPY_CODE', text: 'Copy offer code', code: '' };
}

function normalizeKind(type: string): TemplateButtonKind {
  const t = String(type || 'QUICK_REPLY').toUpperCase().replace(/^WEBSITE$/, 'URL');
  if (t === 'CALL') return 'PHONE_NUMBER';
  if (t === 'COUPON_CODE') return 'COPY_CODE';
  if (t === 'URL' || t === 'PHONE_NUMBER' || t === 'COPY_CODE') return t;
  return 'QUICK_REPLY';
}

export function buttonDraftFromApi(btn: WhatsAppTemplateButton): TemplateButtonDraft | null {
  const kind = normalizeKind(btn.type);
  const text = String(btn.text || '').trim();
  if (!text) return null;
  const id = newButtonId();
  if (kind === 'URL') {
    const hasDeepLink = Boolean(String(btn.deepLinkCampaignId || '').trim());
    return {
      id,
      kind,
      text,
      urlMode: hasDeepLink ? 'deeplink' : 'https',
      url: String(btn.url || 'https://'),
      deepLinkCampaignId: btn.deepLinkCampaignId || null,
      deepLinkUrlType: btn.deepLinkUrlType || 'short',
      deepLinkParamBindings: { ...(btn.deepLinkParamBindings || {}) },
    };
  }
  if (kind === 'PHONE_NUMBER') {
    return { id, kind, text, phoneNumber: String(btn.phoneNumber || '+91') };
  }
  if (kind === 'COPY_CODE') {
    return { id, kind, text, code: String(btn.code || '') };
  }
  return { id, kind: 'QUICK_REPLY', text };
}

export function buttonsFromTemplate(t: WhatsAppTemplate): TemplateButtonDraft[] {
  const rows = (t.buttons || [])
    .map(buttonDraftFromApi)
    .filter(Boolean) as TemplateButtonDraft[];
  return rows.filter((b) => !(b.kind === 'QUICK_REPLY' && b.text.toLowerCase() === 'stop'));
}

export function buttonsToApiPayload(
  buttons: TemplateButtonDraft[],
  opts?: { includeOptOut?: boolean; campaignsById?: Map<string, DeepLinkCampaign> }
): WhatsAppTemplateButton[] {
  const out: WhatsAppTemplateButton[] = [];
  for (const btn of buttons) {
    if (btn.kind === 'URL') {
      const url = resolveUrlButtonTargetUrl(btn, opts?.campaignsById);
      out.push({
        type: 'URL',
        text: btn.text.trim(),
        url,
        ...(btn.urlMode === 'deeplink' && btn.deepLinkCampaignId
          ? {
              deepLinkCampaignId: btn.deepLinkCampaignId,
              deepLinkUrlType: btn.deepLinkUrlType || 'short',
              deepLinkParamBindings: { ...(btn.deepLinkParamBindings || {}) },
            }
          : {}),
      });
    } else if (btn.kind === 'PHONE_NUMBER') {
      out.push({ type: 'PHONE_NUMBER', text: btn.text.trim(), phoneNumber: btn.phoneNumber.trim() });
    } else if (btn.kind === 'COPY_CODE') {
      out.push({ type: 'COPY_CODE', text: btn.text.trim(), code: btn.code.trim() });
    } else if (btn.text.trim()) {
      out.push({ type: 'QUICK_REPLY', text: btn.text.trim() });
    }
  }
  if (opts?.includeOptOut) {
    const hasStop =
      out.some((b) => b.type === 'QUICK_REPLY' && String(b.text || '').toLowerCase() === 'stop') ||
      buttons.some((b) => b.kind === 'QUICK_REPLY' && b.text.toLowerCase() === 'stop');
    if (!hasStop) out.push({ type: 'QUICK_REPLY', text: 'Stop' });
  }
  return out;
}

export function countButtonsByKind(buttons: TemplateButtonDraft[]) {
  return {
    total: buttons.length,
    quickReply: buttons.filter((b) => b.kind === 'QUICK_REPLY').length,
    url: buttons.filter((b) => b.kind === 'URL').length,
    phone: buttons.filter((b) => b.kind === 'PHONE_NUMBER').length,
    copyCode: buttons.filter((b) => b.kind === 'COPY_CODE').length,
  };
}

export function validateTemplateButtons(
  buttons: TemplateButtonDraft[],
  campaignsById?: Map<string, DeepLinkCampaign>
): string | null {
  const counts = countButtonsByKind(buttons);
  if (counts.total > TEMPLATE_BUTTON_MAX_TOTAL) {
    return `Maximum ${TEMPLATE_BUTTON_MAX_TOTAL} buttons allowed (currently ${counts.total})`;
  }
  if (counts.url > TEMPLATE_BUTTON_MAX_URL) {
    return `Maximum ${TEMPLATE_BUTTON_MAX_URL} website URL buttons allowed`;
  }
  if (counts.phone > TEMPLATE_BUTTON_MAX_PHONE) {
    return 'Maximum 1 phone number button allowed';
  }
  if (counts.copyCode > TEMPLATE_BUTTON_MAX_COPY_CODE) {
    return 'Maximum 1 copy code button allowed';
  }

  const labels = new Set<string>();
  for (const btn of buttons) {
    const text = btn.text.trim();
    if (!text) return 'Each button needs a label';
    if (text.length > TEMPLATE_BUTTON_LABEL_MAX) {
      return `Button label "${text.slice(0, 12)}…" exceeds ${TEMPLATE_BUTTON_LABEL_MAX} characters`;
    }
    const key = text.toLowerCase();
    if (labels.has(key)) return `Duplicate button label "${text}"`;
    labels.add(key);

    if (btn.kind === 'URL') {
      if (btn.urlMode === 'deeplink') {
        if (!String(btn.deepLinkCampaignId || '').trim()) {
          return `URL button "${text}" requires a deep link campaign`;
        }
        const resolved = resolveUrlButtonTargetUrl(btn, campaignsById);
        if (!isValidTemplateButtonUrl(resolved)) {
          return `Deep link button "${text}" needs a campaign with a valid https short URL`;
        }
      } else if (!isValidTemplateButtonUrl(btn.url.trim())) {
        return `URL button "${text}" must use a valid https:// link`;
      }
    }
    if (btn.kind === 'PHONE_NUMBER') {
      const digits = btn.phoneNumber.replace(/\D/g, '');
      if (digits.length < 10) return `Phone button "${text}" needs a valid number`;
    }
    if (btn.kind === 'COPY_CODE' && !btn.code.trim()) {
      return `Copy code button "${text}" needs a sample coupon/code`;
    }
  }
  return null;
}

export function previewButtonsFromDraft(
  buttons: TemplateButtonDraft[],
  includeOptOut?: boolean,
  campaignsById?: Map<string, DeepLinkCampaign>
): WhatsAppTemplateButton[] {
  return buttonsToApiPayload(buttons, { includeOptOut, campaignsById });
}
